import { z } from 'zod';
import { AREA_SCOPES, MODULE_KEYS, permissionMatrixSchema, permissionMatrixShape } from './modules';

/** E-mail normalizado (sem espaços nas pontas, minúsculo): o login é único por e-mail. */
export const emailSchema = z
  .string()
  .trim()
  .min(3, 'Informe o e-mail.')
  .max(254, 'E-mail longo demais.')
  .email('E-mail inválido.')
  .transform((v) => v.toLowerCase());

export const PASSWORD_MIN_LENGTH = 10;

/** Regra de senha (mesma validação no app e na API). */
export const passwordSchema = z
  .string()
  .min(PASSWORD_MIN_LENGTH, `A senha precisa ter pelo menos ${PASSWORD_MIN_LENGTH} caracteres.`)
  .max(128, 'A senha pode ter no máximo 128 caracteres.')
  .refine((v) => /[A-Za-zÀ-ÿ]/.test(v), 'A senha precisa ter pelo menos uma letra.')
  .refine((v) => /\d/.test(v), 'A senha precisa ter pelo menos um número.');

/** Perfis do PRD §9 e seus nomes de exibição. */
export const ROLE_LABELS = {
  FLUXUS_ADMIN: 'Administrador Fluxus',
  FLUXUS_CONSULTANT: 'Consultor Fluxus',
  EXECUTIVE: 'Diretor / CEO',
  MANAGER: 'Gerente',
  OPERATION: 'Operação',
} as const;

export type RoleKeyName = keyof typeof ROLE_LABELS;
export const FLUXUS_ROLE_KEYS = ['FLUXUS_ADMIN', 'FLUXUS_CONSULTANT'] as const satisfies RoleKeyName[];
export const CLIENT_ROLE_KEYS = ['EXECUTIVE', 'MANAGER', 'OPERATION'] as const satisfies RoleKeyName[];

/** Plataforma do cliente: define onde o refresh token fica (cookie httpOnly na web; corpo nos apps). */
export const SESSION_PLATFORMS = ['WEB', 'DESKTOP', 'ANDROID', 'IOS'] as const;
export type SessionPlatformName = (typeof SESSION_PLATFORMS)[number];

export const loginRequestSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Informe a senha.').max(128),
  platform: z.enum(SESSION_PLATFORMS).default('WEB'),
});
export type LoginRequest = z.input<typeof loginRequestSchema>;

/** Corpo opcional da renovação/saída: os apps enviam o refresh token; a web usa o cookie. */
export const refreshRequestSchema = z.object({ refreshToken: z.string().min(20).max(200).optional() });

export const authUserSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  email: z.string(),
  role: z.object({
    key: z.enum(['FLUXUS_ADMIN', 'FLUXUS_CONSULTANT', 'EXECUTIVE', 'MANAGER', 'OPERATION']),
    name: z.string(),
  }),
  organization: z.object({ id: z.string().uuid(), name: z.string(), isFluxus: z.boolean() }),
  /** Módulos que o perfil acessa (os que têm "visualizar"): menu e rotas da web. */
  modules: z.array(z.enum(MODULE_KEYS)),
  /** Matriz de permissões do perfil na empresa (T-024): a API confere em cada rota. */
  permissions: permissionMatrixShape,
  /** ALL = toda a empresa; OWN = só itens das suas áreas/unidades. */
  areaScope: z.enum(AREA_SCOPES),
});
export type AuthUser = z.infer<typeof authUserSchema>;

export const authSessionResponseSchema = z.object({
  accessToken: z.string(),
  /** Segundos até o access token expirar. */
  expiresIn: z.number().int(),
  /** Só para DESKTOP/ANDROID/IOS — na web o refresh vai em cookie httpOnly. */
  refreshToken: z.string().optional(),
  user: authUserSchema,
});
export type AuthSessionResponse = z.infer<typeof authSessionResponseSchema>;

export const activeSessionSchema = z.object({
  id: z.string().uuid(),
  platform: z.enum(SESSION_PLATFORMS),
  userAgent: z.string().nullable(),
  ip: z.string().nullable(),
  createdAt: z.string(),
  lastUsedAt: z.string(),
  current: z.boolean(),
});
export type ActiveSession = z.infer<typeof activeSessionSchema>;
export const activeSessionsResponseSchema = z.object({ items: z.array(activeSessionSchema) });

/** Espera do app antes de permitir pedir o link de novo (o servidor aceita um pouco antes disso). */
export const PASSWORD_RESET_RESEND_SECONDS = 60;

/** Pedido de redefinição de senha: a resposta é sempre a mesma (não revela se o e-mail existe). */
export const forgotPasswordRequestSchema = z.object({ email: emailSchema });
export type ForgotPasswordRequest = z.input<typeof forgotPasswordRequestSchema>;
export const forgotPasswordResponseSchema = z.object({ message: z.string() });

/** Token do link de redefinição (vai no fragmento #token= do endereço, nunca na query). */
export const resetTokenSchema = z.string().trim().min(20, 'Link inválido.').max(200, 'Link inválido.');
export const resetTokenCheckSchema = z.object({ token: resetTokenSchema });
export const resetPasswordRequestSchema = z.object({ token: resetTokenSchema, password: passwordSchema });
export type ResetPasswordRequest = z.input<typeof resetPasswordRequestSchema>;

/** Perfil com a sua matriz de permissões (Administração › Perfis e permissões). */
export const rolePermissionsSchema = z.object({
  id: z.string().uuid(),
  key: z.enum(['FLUXUS_ADMIN', 'FLUXUS_CONSULTANT', 'EXECUTIVE', 'MANAGER', 'OPERATION']),
  name: z.string(),
  areaScope: z.enum(AREA_SCOPES),
  permissions: permissionMatrixShape,
  /** Perfil cujas permissões não podem ser reduzidas (Administrador Fluxus: evita trancar a plataforma). */
  locked: z.boolean(),
});
export type RolePermissions = z.infer<typeof rolePermissionsSchema>;

export const organizationPermissionsSchema = z.object({
  organizationId: z.string().uuid(),
  roles: z.array(rolePermissionsSchema),
});

export const updateRolePermissionsSchema = z.object({
  areaScope: z.enum(AREA_SCOPES),
  permissions: permissionMatrixSchema,
  /** Justificativa da alteração (vai para a trilha de auditoria). */
  reason: z.string().trim().max(1000, 'A justificativa pode ter no máximo 1000 caracteres.').optional(),
});
export type UpdateRolePermissions = z.input<typeof updateRolePermissionsSchema>;
